// frontend/src/pages/ListaLubricantes.jsx
// LISTA UNIFICADA DE LUBRICANTES (ACEITES Y ENGRASES)
import React, { useState, useRef } from 'react';
import ListaUnificada from '../components/common/ListaUnificada.jsx';
import { ModuloHeader, ModuloShell } from '../components/common/ModuloHeader.jsx';
import ModalRegistroLubricante from '../components/formularios/ModalRegistroLubricante.jsx';
import { LinkEquipo } from '../components/common/Links.jsx';
import { useToast } from '../components/common/Toast';
import { useConfirm } from '../components/common/ModalConfirmacion';

const HELP = {
  prereqs: [
    'Tener cargados los fluidos de mantenimiento y su repuesto asociado en el almacén.',
  ],
  tips: [
    'Registrá cada carga de lubricante con su cantidad: así se puede seguir el consumo real de cada equipo.',
    'Si el fluido está vinculado a un repuesto, la carga descuenta stock del almacén automáticamente.',
    'Un consumo que sube mes a mes en el mismo equipo suele anticipar una pérdida o un desgaste.',
  ],
  nextSteps: [
    'Consultar el informe de lubricantes para comparar consumos entre equipos.',
    'Revisar el stock del almacén para reponer los lubricantes de mayor rotación.',
  ],
};

const ListaLubricantes = () => {
  const { toast } = useToast();
  const { confirmar } = useConfirm();

  // Estados para formularios React
  const [showModal, setShowModal] = useState(false);
  const [editingRegistro, setEditingRegistro] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  
  // Ref para acceder directamente a ListaUnificada y forzar refresco
  const listaUnificadaRef = useRef();

  // ============================= CONFIGURACIÓN DE COLUMNAS =============================
  // Configuración de columnas basada en el modelo RegistroLubricantes REAL
  const columns = [
    {
      key: 'equipo_interno',
      label: 'Equipo',
      sortable: true,
      sortKey: 'equipo__interno',
      width: '8%',
      render: (value, item) => (
        <LinkEquipo
          href={`/?equipoId=${item.equipo}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.location.href = `/?equipoId=${item.equipo}`;
          }}
        >
          {value}
        </LinkEquipo>
      )
    },
    {
      key: 'equipo_descripcion',
      label: 'Descripción',
      sortable: true,
      sortKey: 'equipo__descripcion',
      width: '10%',
      render: (value) => value || '-'
    },
    {
      key: 'equipo_marca_nombre',
      label: 'Marca',
      sortable: true,
      sortKey: 'equipo__marca__nombre',
      width: '7%',
      render: (value) => value || '-'
    },
    {
      key: 'equipo_modelo_nombre',
      label: 'Modelo',
      sortable: true,
      sortKey: 'equipo__modelo__nombre',
      width: '7%',
      render: (value) => value || '-'
    },
    {
      key: 'equipo_familia_nombre',
      label: 'Familia',
      sortable: true,
      sortKey: 'equipo__familia__nombre',
      width: '7%'
    },
    {
      key: 'fecha',
      label: 'Fecha',
      sortable: true,
      sortKey: 'fecha',
      width: '8%',
      render: (value) => require('../utils/dateUtils').formatDateDisplay(value)
    },
    {
      key: 'lubricante_nombre',
      label: 'Lubricante',
      sortable: true,
      sortKey: 'lubricante__nombre',
      width: '12%',
      render: (value) => value || '-'
    },
    {
      key: 'cantidad',
      label: 'Cantidad',
      sortable: true,
      sortKey: 'cantidad',
      width: '8%',
      render: (value, item) => value ? `${value} ${item.lubricante_unidad_medida || ''}`.trim() : '-'
    },
    {
      key: 'centro_costo_nombre',
      label: 'Centro de Costo',
      sortable: true,
      sortKey: 'centro_costo__nombre',
      width: '9%',
      render: (value) => value || '-'
    },
    {
      key: 'tarea_nombre',
      label: 'Tarea',
      sortable: true,
      sortKey: 'tarea__nombre',
      width: '10%',
      render: (value) => value || '-'
    },
    {
      key: 'mecanico_nombre',
      label: 'Mecánico',
      sortable: true,
      sortKey: 'mecanico__nombre',
      width: '9%',
      render: (value) => value || '-'
    },
    {
      key: 'chofer',
      label: 'Chofer',
      sortable: true,
      sortKey: 'chofer',
      width: '8%',
      render: (value) => value || '-'
    },
    {
      key: 'observaciones',
      label: 'Observaciones',
      sortable: false,
      width: '10%',
      render: (value) => {
        if (!value) return '-';
        return value.length > 40 ? `${value.substring(0, 40)}...` : value;
      }
    }
  ];

  // ============================= CONFIGURACIÓN DE FILTROS =============================
  const filterConfig = [
    {
      key: 'equipo__interno__icontains',
      label: 'Equipo',
      type: 'text',
      placeholder: 'Filtrar por equipo...'
    },
    {
      key: 'equipo__descripcion__icontains',
      label: 'Descripción',
      type: 'text',
      sourceKey: 'equipo_descripcion',
      placeholder: 'Filtrar por descripción...'
    },
    {
      key: 'equipo__marca__nombre__iexact',
      label: 'Marca',
      type: 'text',
      sourceKey: 'equipo_marca_nombre',
      placeholder: 'Filtrar por marca...'
    },
    {
      key: 'equipo__modelo__nombre__iexact',
      label: 'Modelo',
      type: 'text',
      sourceKey: 'equipo_modelo_nombre',
      placeholder: 'Filtrar por modelo...'
    },
    {
      key: 'equipo__familia__nombre__iexact',
      label: 'Familia',
      type: 'text',
      sourceKey: 'equipo_familia_nombre',
      placeholder: 'Filtrar por familia...'
    },
    {
      key: 'fecha__gte',
      label: 'Fecha desde',
      type: 'date',
      placeholder: ''
    },
    {
      key: 'fecha__lte',
      label: 'Fecha hasta',
      type: 'date',
      placeholder: ''
    },
    {
      key: 'lubricante__nombre__icontains',
      label: 'Lubricante',
      type: 'text',
      placeholder: 'Filtrar por lubricante...'
    },
    {
      key: 'centro_costo__nombre__icontains',
      label: 'Centro de Costo',
      type: 'text',
      placeholder: 'Filtrar por centro...'
    },
    {
      key: 'tarea__nombre__icontains',
      label: 'Tarea',
      type: 'text',
      placeholder: 'Filtrar por tarea...'
    },
    {
      key: 'mecanico__nombre__icontains',
      label: 'Mecánico',
      type: 'text',
      placeholder: 'Filtrar por mecánico...'
    },
    {
      key: 'chofer__icontains',
      label: 'Chofer',
      type: 'text',
      placeholder: 'Filtrar por chofer...'
    }
  ];

  // ============================= MANEJADORES DE EVENTOS =============================

  const handleAdd = () => {
    console.log('➕ Agregar registro de lubricante');
    setEditingRegistro(null);
    setShowModal(true);
  };

  const handleEdit = (registro) => {
    console.log('✏️ Editar registro de lubricante:', registro);
    setEditingRegistro(registro);
    setShowModal(true);
  };

  const handleFormSave = async (data) => {
    try {
      // Usar la referencia para refrescar la lista directamente
      if (listaUnificadaRef.current) {
        listaUnificadaRef.current.refreshImmediate();
      }
      
      // Retornar true para indicar que manejamos el cierre
      return true;
      
    } catch (error) {
      console.error('❌ Error guardando registro de lubricante:', error);
    }
  };

  const handleFormCancel = () => {
    console.log('❌ Cancelar formulario');
    setShowModal(false);
    setEditingRegistro(null);
  };

  const handleDelete = async (registro) => {
    const ok = await confirmar({
      titulo: '¿Eliminar registro de lubricante?',
      mensaje: `Se eliminará el registro del ${require('../utils/dateUtils').formatDateDisplay(registro.fecha)}.`,
      labelConfirmar: 'Sí, eliminar',
      variante: 'danger',
    });
    if (!ok) return;

    try {
      console.log('🗑️ Eliminando registro de lubricante:', registro);
      // Aquí iría la lógica de eliminación
    } catch (error) {
      console.error('❌ Error eliminando registro de lubricante:', error);
      toast.error('Error al eliminar el registro de lubricante');
    }
  };

  // ============================= RENDERIZADO =============================

  return (
    <ModuloShell>
      <ModuloHeader
        icono="bi bi-droplet-fill"
        titulo="Lubricantes"
        descripcion="Registros de lubricación de los equipos: aceites, filtros y engrases."
        help={HELP}
        helpOpen={helpOpen}
        onToggleHelp={() => setHelpOpen(v => !v)}
      />

      <ListaUnificada
        ref={listaUnificadaRef}
        cloudScope="lista-lubricantes.state.v1"
        titulo="Registro de Lubricantes"
        barraSeccion
        textoBotonAgregar="Nuevo registro"
        style={{ flex: 1, minHeight: 0 }}
        endpoint="lubricantes/"
        useAdvancedFilters={true}
        tableVisualVariant="rows"
        columns={columns}
        filterConfig={filterConfig}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        searchPlaceholder="Buscar registros de lubricantes..."
        itemsPerPageOptions={[10, 25, 50, 100]}
        defaultItemsPerPage={25}
        allowAdd={true}
        allowEdit={true}
        allowDelete={true}
      />

      {/* Modal del formulario */}
      {showModal && (
        <ModalRegistroLubricante
          isOpen={showModal}
          onClose={handleFormCancel}
          registro={editingRegistro}
          onSaved={handleFormSave}
        />
      )}
    </ModuloShell>
  );
};

export default ListaLubricantes;
